import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Marketer } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
} from "../components/ui";

/** A password handed over on screen, from either creating a sign-in or resetting one. */
interface Credential {
  email: string;
  password: string;
  reason: "created" | "reset";
}

export function Marketers() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [credential, setCredential] = useState<Credential | null>(null);

  const marketers = useQuery({
    queryKey: ["marketers"],
    queryFn: () => api<Marketer[]>("/api/v1/admin/marketers"),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["marketers"] });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/api/v1/admin/marketers", { method: "POST", body }),
    onSuccess: () => {
      setAdding(false);
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api(`/api/v1/admin/marketers/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  // Its own endpoint, not a field on the edit form: deactivating closes the
  // marketer's sessions, and that should never be a side effect of saving a
  // phone number.
  const setActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      api(`/api/v1/admin/marketers/${input.id}/active`, {
        method: "POST",
        body: { active: input.active },
      }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/marketers/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const grantAccess = useMutation({
    mutationFn: (id: string) =>
      api<{ email: string; password: string }>(`/api/v1/admin/marketers/${id}/access`, {
        method: "POST",
        body: {},
      }),
    onSuccess: (result) => {
      setCredential({ ...result, reason: "created" });
      invalidate();
    },
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) =>
      api<{ email: string; password: string }>(`/api/v1/admin/marketers/${id}/access/reset`, {
        method: "POST",
        body: {},
      }),
    onSuccess: (result) => {
      setCredential({ ...result, reason: "reset" });
      invalidate();
    },
  });

  const revokeAccess = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/admin/marketers/${id}/access`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const failure = [update, setActive, remove, grantAccess, resetPassword, revokeAccess].find(
    (mutation) => mutation.isError,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Marketers"
        lede="Who sells on commission, what they have sold, and whether they can sign in."
        action={
          <Button variant="primary" onClick={() => setAdding((value) => !value)}>
            {adding ? "Cancel" : "Add marketer"}
          </Button>
        }
      />

      {credential !== null && (
        <Card title={credential.reason === "created" ? "Portal access created" : "New password"}>
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              Send these to {credential.email}. The password is shown once — only its hash is
              stored, so there is no way to look it up later. Nothing is emailed automatically.
              {credential.reason === "reset" &&
                " Any session they had open has been closed, and their two-factor setup is untouched."}
            </p>
            <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-base text-ink">
              {credential.password}
            </code>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => void navigator.clipboard.writeText(credential.password)}
              >
                Copy password
              </Button>
              <Button onClick={() => setCredential(null)}>I have sent it</Button>
            </div>
          </div>
        </Card>
      )}

      {failure !== undefined && <ErrorNote>{(failure.error as Error).message}</ErrorNote>}

      <Card>
        {adding && (
          <MarketerForm
            submitLabel="Add marketer"
            pending={create.isPending}
            error={create.isError ? (create.error as Error).message : null}
            onSubmit={(body) => create.mutate(body)}
            onCancel={() => setAdding(false)}
          />
        )}

        {marketers.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (marketers.data?.length ?? 0) === 0 ? (
          <EmptyState>
            No marketers yet. Add one before attributing a sale, so the commission has an owner.
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Sales</Th>
                  <Th>Portal access</Th>
                  <Th>Active</Th>
                  <Th className="text-right">Manage</Th>
                </tr>
              </thead>
              <tbody>
                {(marketers.data ?? []).map((marketer) => {
                  const account = marketer.users?.[0];
                  const isEditing = editing === marketer.id;

                  if (isEditing) {
                    return (
                      <tr key={marketer.id} className="border-b border-hairline last:border-0">
                        <td colSpan={6} className="p-0">
                          <MarketerForm
                            submitLabel="Save changes"
                            initial={marketer}
                            pending={update.isPending}
                            error={update.isError ? (update.error as Error).message : null}
                            onSubmit={(body) => update.mutate({ id: marketer.id, body })}
                            onCancel={() => setEditing(null)}
                          />
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={marketer.id} className="border-b border-hairline last:border-0">
                      <Td label="Name">
                        <Link
                          to={`/marketers/${marketer.id}`}
                          className="text-accent underline underline-offset-4"
                        >
                          {marketer.name}
                        </Link>
                      </Td>
                      <Td label="Contact" className="text-muted">
                        <span className="block">{marketer.email}</span>
                        {marketer.phone != null && (
                          <span className="block text-xs text-dim">{marketer.phone}</span>
                        )}
                      </Td>
                      <Td label="Sales" className="tabular text-muted">
                        {marketer._count?.orders ?? 0}
                      </Td>
                      <Td label="Portal access">
                        {account === undefined ? (
                          <Button
                            disabled={grantAccess.isPending || !marketer.active}
                            onClick={() => grantAccess.mutate(marketer.id)}
                          >
                            Create sign-in
                          </Button>
                        ) : (
                          <div className="space-y-1">
                            <span className="block text-xs text-dim">
                              {account.disabledAt !== null
                                ? "Disabled"
                                : account.lastLoginAt === null
                                  ? "Never signed in"
                                  : `Last in ${formatDate(account.lastLoginAt)}`}
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {/* The address is how they sign in, and nothing
                                  is emailed — so a forgotten password is fixed
                                  here rather than through the reset link. */}
                              <Button
                                variant="ghost"
                                disabled={resetPassword.isPending}
                                onClick={() => resetPassword.mutate(marketer.id)}
                              >
                                Reset password
                              </Button>
                              <Button
                                variant="ghost"
                                disabled={revokeAccess.isPending}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Remove the sign-in for ${marketer.email}? Their sales and commission stay exactly as they are.`,
                                    )
                                  ) {
                                    revokeAccess.mutate(marketer.id);
                                  }
                                }}
                              >
                                Remove sign-in
                              </Button>
                            </div>
                          </div>
                        )}
                      </Td>
                      <Td label="Active">
                        {/* Deactivating stops new attributions and closes their
                            sign-in. It leaves every entry already earned exactly
                            where it is. */}
                        <Button
                          variant="ghost"
                          disabled={setActive.isPending}
                          onClick={() =>
                            setActive.mutate({ id: marketer.id, active: !marketer.active })
                          }
                        >
                          <Badge tone={marketer.active ? "live" : "quiet"}>
                            {marketer.active ? "Active" : "Inactive"}
                          </Badge>
                        </Button>
                      </Td>
                      <Td label="Manage" className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button variant="ghost" onClick={() => setEditing(marketer.id)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            disabled={remove.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete ${marketer.name}? This cannot be undone. Anyone with sales on record is refused — deactivate them instead.`,
                                )
                              ) {
                                remove.mutate(marketer.id);
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}

/**
 * Adding and editing, from one form.
 *
 * Two forms for the same six fields drift — one grows a field the other never
 * gets — so the difference is the initial values and the button, and nothing
 * else.
 */
function MarketerForm({
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  initial?: Marketer;
  submitLabel: string;
  pending: boolean;
  error: string | null;
  onSubmit: (body: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  const emailChanged = initial !== undefined && email.trim().toLowerCase() !== initial.email;
  const hasSignIn = (initial?.users?.length ?? 0) > 0;

  return (
    <form
      className="space-y-4 border-b border-hairline p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name,
          email,
          phone: phone.trim().length === 0 ? null : phone.trim(),
          notes: notes.trim().length === 0 ? null : notes.trim(),
        });
      }}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name">
          <Input value={name} required onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Email" hint="Also the address they sign in with, if you give them access.">
          <Input
            type="email"
            value={email}
            required
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChange={(event) => setPhone(event.target.value)} />
        </Field>
        <Field label="Notes">
          <Input value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>

      {emailChanged && hasSignIn && (
        <p className="text-xs text-dim">
          Their sign-in moves to this address too, and any session they have open will end. The
          password does not change.
        </p>
      )}

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? "Saving…" : submitLabel}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
